import { inject, Injectable, InjectionToken } from '@angular/core';

import { APPLICATION_INSIGHTS_LOADER, type ApplicationInsightsLoader } from '../realtime/application-insights-realtime-telemetry';
import type { FilterDimension } from './filter-state.model';

/**
 * Filter-change observability (FR-018, R-013), following the
 * `realtime-telemetry.ts` precedent verbatim: a **closed** schema, frozen
 * records, swallowed sink failures.
 *
 * The privacy property is enforced by the type, not by reviewer vigilance:
 * there is no field — and no method parameter — through which the free-text
 * user identifier, a client code, or an option value could reach a sink.
 * Selecting a client is observable as `{client, set}` without recording *which*
 * tenant, consistent with the existing tenant-free telemetry rule.
 */

export type FilterChangeKind = 'set' | 'cleared' | 'reset';

/**
 * One dimension-level event.
 *
 * Deliberate refinement of data-model §8's flat shape: `reset` is modelled as
 * its own member rather than as a seventh dimension carrying
 * `changeKind: 'reset'`. §8's rules table requires "`reset` emits once, not
 * seven times", and under a flat shape a reset would have to name some
 * arbitrary dimension. Both enums stay closed and no value field is
 * expressible, which is what FR-018/R-013 actually constrain.
 */
export type FilterChangeEvent =
  | {
      readonly dimension: FilterDimension;
      readonly changeKind: Exclude<FilterChangeKind, 'reset'>;
      /** Epoch ms. */
      readonly at: number;
    }
  | {
      readonly changeKind: 'reset';
      /** Epoch ms. */
      readonly at: number;
    };

export abstract class FilterTelemetrySink {
  abstract emit(record: FilterChangeEvent): void | Promise<void>;
}

export const FILTER_TELEMETRY_SINK = new InjectionToken<FilterTelemetrySink>(
  'FILTER_TELEMETRY_SINK',
);

/**
 * Event timestamps. A plain epoch-ms reader rather than `ClockService`: the
 * clock service exists to drive *state* at one-second granularity and starts a
 * shared interval on first read — an observability stamp needs neither.
 */
export const FILTER_TELEMETRY_CLOCK = new InjectionToken<() => number>(
  'FILTER_TELEMETRY_CLOCK',
  { factory: () => () => Date.now() },
);

@Injectable()
export class FilterTelemetry {
  private readonly sink = inject(FILTER_TELEMETRY_SINK);
  private readonly now = inject(FILTER_TELEMETRY_CLOCK);

  /** One event per dimension per published change (obligation 18). */
  dimensionChanged(
    dimension: FilterDimension,
    changeKind: Exclude<FilterChangeKind, 'reset'>,
  ): void {
    this.emit({ dimension, changeKind, at: this.now() });
  }

  /** One event for the whole reset — never one per dimension (FR-014, obligation 18). */
  reset(): void {
    this.emit({ changeKind: 'reset', at: this.now() });
  }

  private emit(record: FilterChangeEvent): void {
    const frozenRecord = Object.freeze(record);
    try {
      const result = this.sink.emit(frozenRecord);
      if (result instanceof Promise) void result.catch(() => undefined);
    } catch {
      // Telemetry is deliberately isolated from the filter lifecycle: a sink
      // failure can never alter a published scope.
    }
  }
}

export class ConsoleFilterTelemetrySink extends FilterTelemetrySink {
  override emit(record: FilterChangeEvent): void {
    console.info('[filters]', record);
  }
}

interface ApplicationInsightsEventClient {
  loadAppInsights(): void;
  trackEvent(
    event: { readonly name: string },
    properties?: Readonly<Record<string, string | number>>,
  ): void;
}

/**
 * Reuses the existing `APPLICATION_INSIGHTS_LOADER` token (Art. X.2) — no new
 * dependency, no second lazy import of the SDK, and the same auto-collection
 * disabling the realtime sink applies, so nothing outside the allowlisted
 * record above can reach the wire.
 */
export class ApplicationInsightsFilterTelemetrySink extends FilterTelemetrySink {
  private clientPromise: Promise<ApplicationInsightsEventClient> | null = null;

  constructor(
    private readonly connectionString: string,
    private readonly loader: ApplicationInsightsLoader,
  ) {
    super();
  }

  override async emit(record: FilterChangeEvent): Promise<void> {
    const client = await this.getClient();
    client.trackEvent(
      { name: `AITH.Filters.${record.changeKind}` },
      'dimension' in record
        ? { dimension: record.dimension, at: record.at }
        : { at: record.at },
    );
  }

  private getClient(): Promise<ApplicationInsightsEventClient> {
    this.clientPromise ??= this.loader().then((module) => {
      const client = new module.ApplicationInsights({
        config: {
          connectionString: this.connectionString,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
        },
      });
      client.loadAppInsights();
      return client;
    });
    return this.clientPromise;
  }
}

/** Selection mirrors `createRealtimeTelemetrySink`: console when unconfigured. */
export function createFilterTelemetrySink(
  connectionString: string | null,
  loader: ApplicationInsightsLoader = inject(APPLICATION_INSIGHTS_LOADER),
): FilterTelemetrySink {
  return connectionString === null
    ? new ConsoleFilterTelemetrySink()
    : new ApplicationInsightsFilterTelemetrySink(connectionString, loader);
}
